import './game.css';
import '../party/party.css';
import { relatedToolsMarkup, siteBrandMarkup } from '../site-chrome';
import { kakaoCardShareMessage, preloadKakaoShare } from '../kakao-share';
import { PartyNetwork, type PartyConnectionState } from '../party/network';
import {
  normalizePartyRoomCode,
  type PartyPublicRoomSummary,
  type PartyRoomClosedReason,
  type PartyRoomView,
  type PartyServerMessage,
} from '../party/protocol';
import {
  PARTY_PLAYER_COLORS,
  copyPartyText,
  escapePartyHtml,
  inviteRoomFromLocation,
  partyEntryMarkup,
  partyInviteUrl,
  partyLobbyMarkup,
  savedPartyNickname,
  savePartyNickname,
  sharePartyInvite,
  updatePartyLobbyAutoStart,
  type PartyEntryTab,
} from '../party/ui';
import {
  applyTailChaseBoardDelta,
  isTailChaseView,
  tailChaseOppositeDirection,
  type TailChaseDirection,
  type TailChasePlayerView,
  type TailChaseView,
} from './core';

const GAME_PATH = '/games/wavebean-tail-chase/';
const GAME_TITLE = '파도콩 꼬리잡기';
const ARENA_WIDTH = 960;
const ARENA_HEIGHT = 560;

function requireElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`${selector} 요소를 찾지 못했습니다.`);
  return element;
}

function connectionCopy(state: PartyConnectionState): string {
  if (state === 'connected') return '서버 연결됨';
  if (state === 'connecting') return '연결하는 중';
  if (state === 'reconnecting') return '재접속하는 중';
  return '연결 대기';
}

function closedReasonCopy(reason: PartyRoomClosedReason): string {
  if (reason === 'left') return '방에서 나왔습니다.';
  if (reason === 'room-expired') return '오랫동안 사용하지 않아 방이 종료됐습니다.';
  return '서버 점검으로 방이 종료됐습니다. 잠시 후 다시 시도해 주세요.';
}

function mix(from: number, to: number, amount: number): number {
  return from + (to - from) * amount;
}

export function mountTailChase(): void {
  preloadKakaoShare();
  const app = requireElement<HTMLDivElement>('#app');
  document.documentElement.classList.add('party-page', 'tail-chase-page');
  document.title = `${GAME_TITLE} | 오늘툴`;

  app.innerHTML = `
    <div class="party-shell tail-shell">
      <header class="site-brand-header party-header">
        ${siteBrandMarkup()}
        <span class="party-header-badge">2–4인 · 실시간 대전</span>
      </header>

      <main>
        <section class="party-intro tail-intro">
          <span>TURN · DODGE · SURVIVE</span>
          <h1>파도콩 꼬리잡기</h1>
          <p>가고 싶은 화면 방향으로 파도콩을 틀어 빛나는 꼬리와 벽을 피하세요.<br />경기당 한 번의 부스터를 활용해 먼저 3점을 얻으면 승리!</p>
        </section>

        <section class="party-card tail-card" aria-label="파도콩 꼬리잡기 게임">
          <div class="party-card-bar">
            <span id="party-connection" class="party-connection offline">연결 대기</span>
            <div id="party-room-mini" class="party-room-mini" hidden>
              <span>방</span><strong id="party-room-mini-code"></strong>
              <button type="button" data-party-action="copy-code">코드 복사</button>
            </div>
          </div>
          <div id="party-surface" class="party-surface tail-surface"></div>
        </section>

        <section class="party-help" aria-label="게임 방법">
          <article><strong>1. 가고 싶은 방향을 눌러요</strong><p>방향키나 W/A/S/D가 화면의 위·왼쪽·아래·오른쪽과 그대로 연결돼요.</p></article>
          <article><strong>2. 모든 꼬리를 피해요</strong><p>벽, 내 꼬리, 친구의 꼬리 어디든 닿으면 이번 라운드에서 탈락해요.</p></article>
          <article><strong>3. 부스터는 딱 한 번</strong><p>스페이스바나 번개 버튼으로 1.5초 가속해요. 3점 승부 전체에서 한 번만 쓸 수 있어요.</p></article>
        </section>

        ${relatedToolsMarkup('wavebean-tail-chase')}
      </main>

      <footer class="party-footer">
        <a href="/privacy/">개인정보처리방침</a>
        <a href="/terms/">이용약관</a>
        <span>방과 경기 기록은 서버 메모리에만 잠시 보관돼요.</span>
      </footer>
    </div>`;

  const surface = requireElement<HTMLDivElement>('#party-surface');
  const connectionBadge = requireElement<HTMLSpanElement>('#party-connection');
  const roomMini = requireElement<HTMLDivElement>('#party-room-mini');
  const roomMiniCode = requireElement<HTMLElement>('#party-room-mini-code');
  const incomingRoom = inviteRoomFromLocation();

  let connection: PartyConnectionState = 'offline';
  let room: PartyRoomView | null = null;
  let currentView: TailChaseView | null = null;
  let previousHeads = new Map<string, { x: number; y: number }>();
  let renderedHeads = new Map<string, { x: number; y: number }>();
  let snapshotAt = performance.now();
  let serverClockDelta = 0;
  let notice = '';
  let currentSurfaceKey = '';
  let entryTab: PartyEntryTab = 'private';
  let entryNickname = savedPartyNickname();
  let publicRooms: readonly PartyPublicRoomSummary[] = [];
  let publicRoomsLoaded = false;
  let arenaCanvas: HTMLCanvasElement | null = null;
  let arenaContext: CanvasRenderingContext2D | null = null;
  let boostPending = false;

  const network = new PartyNetwork('tail-chase', {
    onConnection(nextConnection) {
      connection = nextConnection;
      if (nextConnection !== 'connected') boostPending = false;
      updateConnection();
      updateReconnectOverlay();
      if (!room) renderSurface(true);
      else updateArenaHud();
    },
    onMessage(message) {
      handleMessage(message);
    },
  });

  function ownRoomPlayer() {
    return room?.players.find((player) => player.id === network.playerId) ?? null;
  }

  function ownGamePlayer() {
    return currentView?.players.find((player) => player.id === network.playerId) ?? null;
  }

  function playerName(playerId: string | null): string {
    if (!playerId) return '';
    return currentView?.players.find((player) => player.id === playerId)?.nickname ?? '파도콩';
  }

  function updateConnection(): void {
    connectionBadge.textContent = connectionCopy(connection);
    connectionBadge.className = `party-connection ${connection === 'connected' ? '' : connection === 'offline' ? 'offline' : 'connecting'}`.trim();
  }

  function updateRoomMini(): void {
    roomMini.hidden = !room;
    roomMiniCode.textContent = room?.roomCode ?? '';
  }

  function updateUrl(roomCode: string | null): void {
    const url = new URL(window.location.href);
    if (roomCode) url.searchParams.set('room', roomCode);
    else url.searchParams.delete('room');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }

  function surfaceKey(): string {
    if (!room) {
      const rooms = publicRooms.map((candidate) => `${candidate.roomCode}:${candidate.playerCount}:${candidate.autoStartAt ?? ''}`).join('|');
      return `entry:${incomingRoom ?? ''}:${entryTab}:${publicRoomsLoaded}:${rooms}:${notice}:${connection}`;
    }
    if (room.phase === 'lobby') {
      const players = room.players.map((player) => `${player.id}:${player.ready}:${player.connected}:${player.isHost}`).join('|');
      return `lobby:${room.visibility}:${room.hostId}:${players}:${room.autoStartAt ?? ''}:${notice}`;
    }
    if (!currentView) return `invalid:${notice}`;
    if (currentView.mode === 'complete') return `complete:${currentView.phaseId}:${room.rematchReadyPlayerIds.join(',')}:${notice}`;
    return `arena:${currentView.matchId}:${currentView.phaseId}`;
  }

  function renderSurface(force = false): void {
    const key = surfaceKey();
    if (!force && key === currentSurfaceKey) {
      updateRoomMini();
      updateArenaHud();
      updateReconnectOverlay();
      return;
    }
    currentSurfaceKey = key;
    arenaCanvas = null;
    arenaContext = null;
    if (!room) {
      surface.innerHTML = partyEntryMarkup({
        incomingRoom,
        title: '빛나는 꼬리 사이를 달려볼까요?',
        description: '가입 없이 닉네임만 정하면 돼요. 2명부터 바로 시작할 수 있어요.',
        notice,
        busy: connection === 'connecting' || connection === 'reconnecting',
        activeTab: entryTab,
        nicknameValue: entryNickname,
        publicRooms,
        publicRoomsLoaded,
      });
    } else if (room.phase === 'lobby') {
      surface.innerHTML = partyLobbyMarkup({
        room,
        selfId: network.playerId,
        gameLabel: '꼬리잡기',
        startLabel: '꼬리잡기 시작!',
        notice,
      });
    } else if (!currentView) {
      surface.innerHTML = `
        <section class="party-panel center"><span class="party-eyebrow">새로고침이 필요해요</span><h2>경기 상태를 읽지 못했습니다</h2>
        <p>페이지를 새로고침해 같은 방으로 다시 연결해 주세요.</p><button class="party-primary" type="button" data-party-action="reload">새로고침</button></section>`;
    } else if (currentView.mode === 'complete') {
      renderComplete();
    } else {
      renderArena();
    }
    updateRoomMini();
    updateReconnectOverlay();
  }

  function playerCardMarkup(player: TailChasePlayerView): string {
    const color = PARTY_PLAYER_COLORS[player.colorIndex % PARTY_PLAYER_COLORS.length];
    const connected = room?.players.find((candidate) => candidate.id === player.id)?.connected ?? false;
    const scoreDots = Array.from({ length: currentView?.targetScore ?? 3 }, (_, index) => `<i class="${index < player.score ? 'filled' : ''}"></i>`).join('');
    return `
      <article class="tail-player-card ${player.id === network.playerId ? 'self' : ''} ${player.alive ? '' : 'eliminated'} ${connected ? '' : 'disconnected'}" style="--tail-player-color:${color}">
        <span class="party-bean-avatar" style="--party-player-color:${color}" aria-hidden="true"><i></i></span>
        <span><strong>${escapePartyHtml(player.nickname)}${player.id === network.playerId ? ' · 나' : ''}</strong><small>${player.alive ? connected ? '생존 중' : '재접속 중' : '이번 라운드 탈락'}</small></span>
        <span class="tail-score-dots" aria-label="${player.score}점">${scoreDots}</span>
      </article>`;
  }

  function renderArena(): void {
    if (!currentView) return;
    surface.innerHTML = `
      <section class="tail-game-panel">
        <div class="tail-round-bar">
          <strong>ROUND ${currentView.roundId}</strong>
          <span id="tail-round-status">${currentView.mode === 'round' ? '마지막까지 살아남으세요' : '라운드 준비'}</span>
          <b id="tail-round-timer">--</b>
        </div>
        <div id="tail-scoreboard" class="tail-scoreboard">${currentView.players.map(playerCardMarkup).join('')}</div>
        <div class="tail-arena-frame">
          <canvas id="tail-arena" width="${ARENA_WIDTH}" height="${ARENA_HEIGHT}" aria-label="파도콩 꼬리잡기 경기장"></canvas>
          <div id="tail-overlay" class="tail-overlay" role="status"></div>
        </div>
        <div class="tail-controls" aria-label="방향 조작">
          <div class="tail-dpad" role="group" aria-label="절대 방향 조작">
            <button class="up" type="button" data-tail-direction="3" aria-label="위로 방향 전환"><span>↑</span><small>W</small></button>
            <button class="left" type="button" data-tail-direction="2" aria-label="왼쪽으로 방향 전환"><span>←</span><small>A</small></button>
            <i aria-hidden="true">●</i>
            <button class="right" type="button" data-tail-direction="0" aria-label="오른쪽으로 방향 전환"><span>→</span><small>D</small></button>
            <button class="down" type="button" data-tail-direction="1" aria-label="아래로 방향 전환"><span>↓</span><small>S</small></button>
          </div>
          <div class="tail-control-guide"><strong>화면 방향 그대로!</strong><small>방향키 · W A S D</small></div>
          <button class="tail-boost-button" type="button" data-tail-boost aria-label="경기당 한 번 부스터 사용"><span>⚡</span><strong>부스터</strong><small id="tail-boost-status">경기당 1회 · SPACE</small></button>
        </div>
        <p class="tail-live-notice party-notice" role="status">${escapePartyHtml(notice)}</p>
      </section>`;
    arenaCanvas = requireElement<HTMLCanvasElement>('#tail-arena');
    arenaContext = arenaCanvas.getContext('2d');
    resizeArenaCanvas();
    updateArenaHud();
  }

  function renderComplete(): void {
    if (!currentView || currentView.mode !== 'complete') return;
    const completeView = currentView;
    const winner = completeView.players.find((player) => player.id === completeView.matchWinnerId) ?? null;
    const rematchReady = room?.rematchReadyPlayerIds.includes(network.playerId ?? '') ?? false;
    const ordered = [...completeView.players].sort((left, right) => right.score - left.score);
    surface.innerHTML = `
      <section class="tail-result-panel">
        <span class="party-eyebrow">MATCH COMPLETE</span>
        <div class="tail-winner-bean" style="--party-player-color:${PARTY_PLAYER_COLORS[(winner?.colorIndex ?? 0) % PARTY_PLAYER_COLORS.length]}"><span class="party-bean-avatar"><i></i></span><b>♛</b></div>
        <h2>${winner ? `${escapePartyHtml(winner.nickname)} 승리!` : '이번 경기는 무승부!'}</h2>
        <p>${winner ? '빛나는 꼬리 사이에서 마지막까지 살아남았어요.' : '다시 한 번 승부를 내볼까요?'}</p>
        <div class="tail-final-scores">
          ${ordered.map((player, index) => `<div><span>${index + 1}</span><strong>${escapePartyHtml(player.nickname)}</strong><b>${player.score}점</b></div>`).join('')}
        </div>
        <div class="tail-rematch-box">
          <strong>${room?.rematchReadyPlayerIds.length ?? 0}/${room?.players.filter((player) => player.connected).length ?? 0}명 재대결 준비</strong>
          <button class="party-primary full coral" type="button" data-party-action="rematch" ${rematchReady ? 'disabled' : ''}>${rematchReady ? '친구들을 기다리는 중' : '한 판 더!'}</button>
        </div>
        <button class="party-leave-button" type="button" data-party-action="leave">방 나가기</button>
        <p class="party-notice" role="status">${escapePartyHtml(notice)}</p>
      </section>`;
  }

  function updateArenaHud(): void {
    if (room?.phase === 'lobby') {
      updatePartyLobbyAutoStart(surface, room, Date.now() + serverClockDelta);
      return;
    }
    if (!currentView || currentView.mode === 'complete') return;
    const scoreboard = document.querySelector<HTMLDivElement>('#tail-scoreboard');
    if (scoreboard) scoreboard.innerHTML = currentView.players.map(playerCardMarkup).join('');
    const now = Date.now() + serverClockDelta;
    const remaining = Math.max(0, currentView.deadlineAt - now);
    const timer = document.querySelector<HTMLElement>('#tail-round-timer');
    const status = document.querySelector<HTMLElement>('#tail-round-status');
    const overlay = document.querySelector<HTMLDivElement>('#tail-overlay');
    const ownPlayer = ownGamePlayer();
    const boosting = (ownPlayer?.boostUntil ?? 0) > now;
    if (timer) timer.textContent = currentView.mode === 'round' ? `${Math.ceil(remaining / 1_000)}초` : `R${currentView.roundId}`;
    if (status) {
      status.textContent = currentView.mode === 'round'
        ? ownPlayer?.alive ? boosting ? '부스터 가속 중!' : currentView.speedLevel === 2 ? '속도 상승! 끝까지 버티세요' : '마지막까지 살아남으세요' : '다음 라운드를 기다리는 중'
        : currentView.mode === 'countdown' ? '곧 출발합니다' : '라운드 종료';
    }
    if (overlay) {
      if (currentView.mode === 'countdown') {
        overlay.className = 'tail-overlay visible countdown';
        overlay.innerHTML = `<small>ROUND ${currentView.roundId}</small><strong>${Math.max(1, Math.ceil(remaining / 800))}</strong><span>방향을 정하세요!</span>`;
      } else if (currentView.mode === 'round-end') {
        overlay.className = 'tail-overlay visible round-end';
        overlay.innerHTML = currentView.winnerId
          ? `<small>ROUND WINNER</small><strong>${escapePartyHtml(playerName(currentView.winnerId))}</strong><span>${Math.ceil(remaining / 1_000)}초 뒤 다음 라운드</span>`
          : `<small>DRAW</small><strong>동시 탈락!</strong><span>${Math.ceil(remaining / 1_000)}초 뒤 다시 시작</span>`;
      } else {
        overlay.className = 'tail-overlay';
        overlay.innerHTML = '';
      }
    }
    const canControl = connection === 'connected' && currentView.mode === 'round' && (ownPlayer?.alive ?? false);
    const opposite = ownPlayer ? tailChaseOppositeDirection(ownPlayer.direction) : null;
    surface.querySelectorAll<HTMLButtonElement>('[data-tail-direction]').forEach((button) => {
      const direction = Number(button.dataset.tailDirection) as TailChaseDirection;
      const current = direction === ownPlayer?.direction;
      button.disabled = !canControl || direction === opposite;
      button.classList.toggle('current', current);
      button.classList.toggle('reverse', direction === opposite);
      button.setAttribute('aria-pressed', String(current));
    });
    const boostButton = surface.querySelector<HTMLButtonElement>('[data-tail-boost]');
    const boostStatus = surface.querySelector<HTMLElement>('#tail-boost-status');
    if (boostButton) {
      boostButton.disabled = !canControl || boostPending || boosting || !(ownPlayer?.boostAvailable ?? false);
      boostButton.classList.toggle('active', boosting);
      boostButton.classList.toggle('used', !boosting && !(ownPlayer?.boostAvailable ?? false));
    }
    if (boostStatus) {
      boostStatus.textContent = boosting
        ? `${Math.max(0, (ownPlayer!.boostUntil - now) / 1_000).toFixed(1)}초 남음`
        : ownPlayer?.boostAvailable ? boostPending ? '발동 중…' : '경기당 1회 · SPACE' : '사용 완료';
    }
    const noticeElement = surface.querySelector<HTMLElement>('.tail-live-notice');
    if (noticeElement) noticeElement.textContent = notice;
  }

  function updateReconnectOverlay(): void {
    surface.querySelector('.tail-reconnect-overlay')?.remove();
    if (!room || connection === 'connected') return;
    const overlay = document.createElement('div');
    overlay.className = 'tail-reconnect-overlay';
    overlay.innerHTML = `<strong>${connection === 'reconnecting' ? '재접속하는 중…' : '연결이 끊겼어요'}</strong><span>경기는 계속 진행되며 연결되면 현재 위치로 돌아와요.</span>${connection === 'offline' ? '<div><button type="button" data-party-action="retry-connection">다시 연결</button><button type="button" data-party-action="leave">방 나가기</button></div>' : ''}`;
    surface.append(overlay);
  }

  function resizeArenaCanvas(): void {
    if (!arenaCanvas || !arenaContext) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    arenaCanvas.width = Math.round(ARENA_WIDTH * dpr);
    arenaCanvas.height = Math.round(ARENA_HEIGHT * dpr);
    arenaContext.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function drawBean(
    context: CanvasRenderingContext2D,
    player: TailChasePlayerView,
    x: number,
    y: number,
    size: number,
    boosting: boolean,
  ): void {
    const color = PARTY_PLAYER_COLORS[player.colorIndex % PARTY_PLAYER_COLORS.length];
    context.save();
    context.translate(x, y);
    context.rotate(player.direction * Math.PI / 2);
    context.globalAlpha = player.alive ? 1 : 0.45;
    context.shadowColor = color;
    context.shadowBlur = player.alive ? boosting ? 34 : 18 : 0;
    if (boosting) {
      context.strokeStyle = 'rgba(255, 244, 137, .82)';
      context.lineWidth = Math.max(3, size * .15);
      context.beginPath();
      context.ellipse(0, 0, size * .84, size * .7, -.12, 0, Math.PI * 2);
      context.stroke();
    }
    context.fillStyle = color;
    context.strokeStyle = '#0c1830';
    context.lineWidth = Math.max(2, size * 0.13);
    context.beginPath();
    context.ellipse(0, 0, size * 0.62, size * 0.5, -.12, 0, Math.PI * 2);
    context.fill();
    context.stroke();
    context.shadowBlur = 0;
    context.fillStyle = '#0c1830';
    context.beginPath();
    context.arc(size * 0.24, -size * 0.17, size * 0.07, 0, Math.PI * 2);
    context.arc(size * 0.28, size * 0.16, size * 0.07, 0, Math.PI * 2);
    context.fill();
    if (!player.alive) {
      context.strokeStyle = '#fff';
      context.lineWidth = 2;
      context.beginPath();
      context.moveTo(-size * .24, -size * .24);
      context.lineTo(size * .24, size * .24);
      context.moveTo(size * .24, -size * .24);
      context.lineTo(-size * .24, size * .24);
      context.stroke();
    }
    context.restore();
  }

  function drawArena(now: number): void {
    if (!arenaContext || !currentView || currentView.mode === 'complete') return;
    const context = arenaContext;
    const gradient = context.createLinearGradient(0, 0, ARENA_WIDTH, ARENA_HEIGHT);
    gradient.addColorStop(0, '#0d1c36');
    gradient.addColorStop(1, '#172b4d');
    context.fillStyle = gradient;
    context.fillRect(0, 0, ARENA_WIDTH, ARENA_HEIGHT);

    const cell = Math.min((ARENA_WIDTH - 46) / currentView.boardWidth, (ARENA_HEIGHT - 46) / currentView.boardHeight);
    const boardWidth = cell * currentView.boardWidth;
    const boardHeight = cell * currentView.boardHeight;
    const originX = (ARENA_WIDTH - boardWidth) / 2;
    const originY = (ARENA_HEIGHT - boardHeight) / 2;
    context.strokeStyle = 'rgba(122, 207, 220, .08)';
    context.lineWidth = 1;
    context.beginPath();
    for (let x = 0; x <= currentView.boardWidth; x += 4) {
      context.moveTo(originX + x * cell, originY);
      context.lineTo(originX + x * cell, originY + boardHeight);
    }
    for (let y = 0; y <= currentView.boardHeight; y += 4) {
      context.moveTo(originX, originY + y * cell);
      context.lineTo(originX + boardWidth, originY + y * cell);
    }
    context.stroke();

    context.strokeStyle = 'rgba(116, 232, 222, .45)';
    context.lineWidth = 2;
    context.strokeRect(originX - 3, originY - 3, boardWidth + 6, boardHeight + 6);

    const board = currentView.board ?? '0'.repeat(currentView.boardWidth * currentView.boardHeight);
    const serverNow = Date.now() + serverClockDelta;
    for (let index = 0; index < board.length; index += 1) {
      const owner = board.charCodeAt(index) - 48;
      if (owner <= 0) continue;
      const player = currentView.players[owner - 1];
      if (!player) continue;
      const x = index % currentView.boardWidth;
      const y = Math.floor(index / currentView.boardWidth);
      const color = PARTY_PLAYER_COLORS[player.colorIndex % PARTY_PLAYER_COLORS.length];
      context.fillStyle = color;
      context.globalAlpha = .78;
      context.shadowColor = color;
      context.shadowBlur = player.boostUntil > serverNow ? 13 : 7;
      context.fillRect(originX + x * cell + cell * .14, originY + y * cell + cell * .14, cell * .72, cell * .72);
      context.shadowBlur = 0;
      if (player.colorIndex % 2 === 1) {
        context.fillStyle = 'rgba(255,255,255,.38)';
        context.fillRect(originX + x * cell + cell * .38, originY + y * cell + cell * .14, cell * .18, cell * .72);
      }
    }
    context.globalAlpha = 1;

    const interpolation = Math.min(1, Math.max(0, (now - snapshotAt) / 100));
    for (const player of currentView.players) {
      const previous = previousHeads.get(player.id) ?? player;
      const x = mix(previous.x, player.x, interpolation);
      const y = mix(previous.y, player.y, interpolation);
      renderedHeads.set(player.id, { x, y });
      drawBean(
        context,
        player,
        originX + (x + .5) * cell,
        originY + (y + .5) * cell,
        Math.max(15, cell * 1.5),
        player.boostUntil > serverNow,
      );
    }
    context.fillStyle = 'rgba(255,255,255,.28)';
    context.font = '800 10px sans-serif';
    context.fillText('WAVEBEAN NIGHT GRID', originX + 8, originY + 18);
  }

  function handleRoomUpdate(nextRoom: PartyRoomView): void {
    const rawView = isTailChaseView(nextRoom.game) ? nextRoom.game : null;
    const boardLength = rawView ? rawView.boardWidth * rawView.boardHeight : 0;
    const materializedBoard = rawView?.board ?? (rawView && currentView?.matchId === rawView.matchId
      ? applyTailChaseBoardDelta(
        rawView.resetBoard ? '0'.repeat(boardLength) : currentView.board ?? '0'.repeat(boardLength),
        rawView.boardDelta,
        boardLength,
      )
      : null);
    const nextView = rawView && materializedBoard !== null
      ? { ...rawView, board: materializedBoard }
      : null;
    if (currentView && nextView
      && currentView.matchId === nextView.matchId
      && currentView.roundId === nextView.roundId) {
      const nextPreviousHeads = new Map<string, { x: number; y: number }>();
      for (const player of nextView.players) {
        const authoritativePrevious = currentView.players.find((candidate) => candidate.id === player.id);
        const point = renderedHeads.get(player.id) ?? authoritativePrevious ?? player;
        nextPreviousHeads.set(player.id, { x: point.x, y: point.y });
      }
      previousHeads = nextPreviousHeads;
    } else if (nextView) {
      previousHeads = new Map(nextView.players.map((player) => [player.id, { x: player.x, y: player.y }]));
      renderedHeads = new Map(previousHeads);
    } else {
      previousHeads = new Map();
      renderedHeads = new Map();
    }
    snapshotAt = performance.now();
    room = nextRoom;
    currentView = nextView;
    const ownNextPlayer = nextView?.players.find((player) => player.id === network.playerId);
    if (!ownNextPlayer?.boostAvailable || nextView?.mode !== 'round') boostPending = false;
    serverClockDelta = nextRoom.serverTime - Date.now();
    notice = '';
    updateUrl(nextRoom.roomCode);
    renderSurface();
  }

  function handleMessage(message: PartyServerMessage): void {
    if (message.type === 'room' || message.type === 'state') {
      handleRoomUpdate(message.room);
    } else if (message.type === 'public-rooms' && message.gameId === 'tail-chase') {
      publicRooms = message.rooms;
      publicRoomsLoaded = true;
      renderSurface();
    } else if (message.type === 'error') {
      notice = message.message;
      boostPending = false;
      if (room?.phase === 'playing') updateArenaHud();
      else renderSurface(true);
    } else if (message.type === 'closed') {
      room = null;
      currentView = null;
      boostPending = false;
      notice = closedReasonCopy(message.reason);
      updateUrl(null);
      renderSurface(true);
      if (entryTab === 'public') window.setTimeout(() => network.requestPublicRooms(), 150);
    }
  }

  function inviteUrl(): string {
    return partyInviteUrl(GAME_PATH, room?.roomCode ?? '');
  }

  async function copyInvite(): Promise<void> {
    if (!room) return;
    notice = await copyPartyText(inviteUrl()) ? '초대 링크를 복사했어요.' : '링크를 복사하지 못했습니다.';
    renderSurface(true);
  }

  async function shareInvite(): Promise<void> {
    if (!room) return;
    const mode = await sharePartyInvite(GAME_TITLE, `${room.roomCode} 방에서 같이 꼬리잡기 해요!`, inviteUrl());
    notice = kakaoCardShareMessage(mode);
    renderSurface(true);
  }

  function sendDirection(direction: TailChaseDirection): void {
    if (!currentView || currentView.mode !== 'round' || !ownGamePlayer()?.alive || connection !== 'connected') return;
    network.action({ type: 'direction', roundId: currentView.roundId, direction });
  }

  function activateBoost(): void {
    const player = ownGamePlayer();
    if (!currentView || currentView.mode !== 'round' || !player?.alive || connection !== 'connected'
      || !player.boostAvailable || player.boostUntil > Date.now() + serverClockDelta || boostPending) return;
    boostPending = true;
    updateArenaHud();
    network.action({ type: 'boost', roundId: currentView.roundId });
  }

  surface.addEventListener('submit', (event) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    if (form.id !== 'party-entry-form') return;
    const submitter = (event as SubmitEvent).submitter as HTMLButtonElement | null;
    const nickname = requireElement<HTMLInputElement>('#party-nickname').value.trim();
    const mode = submitter?.dataset.entryMode ?? 'create-private';
    if (!nickname) {
      notice = '닉네임을 입력해 주세요.';
      renderSurface(true);
      return;
    }
    savePartyNickname(nickname);
    entryNickname = nickname;
    notice = '';
    if (mode === 'join-private') {
      const roomCode = normalizePartyRoomCode(requireElement<HTMLInputElement>('#party-room-code').value);
      if (roomCode.length !== 6) {
        notice = '6자리 방 코드를 입력해 주세요.';
        renderSurface(true);
        return;
      }
      network.joinRoom(roomCode, nickname);
    } else if (mode === 'join-public') {
      const roomCode = normalizePartyRoomCode(submitter?.dataset.roomCode ?? '');
      if (roomCode.length !== 6) return;
      network.joinRoom(roomCode, nickname);
    } else if (mode === 'quick-public') {
      network.quickJoin(nickname);
    } else if (mode === 'create-public') {
      network.createRoom(nickname, 'public');
    } else {
      network.createRoom(nickname, 'private');
    }
  });

  surface.addEventListener('input', (event) => {
    const input = event.target as HTMLInputElement;
    if (input.id === 'party-nickname') entryNickname = input.value;
    else if (input.id === 'party-room-code') input.value = normalizePartyRoomCode(input.value);
  });

  surface.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-party-action], [data-party-entry-tab], [data-tail-direction], [data-tail-boost]');
    if (!target) return;
    const nextEntryTab = target.dataset.partyEntryTab as PartyEntryTab | undefined;
    if (nextEntryTab) {
      entryTab = nextEntryTab;
      notice = '';
      renderSurface(true);
      if (entryTab === 'public') network.requestPublicRooms();
      return;
    }
    if (target.dataset.tailDirection) {
      sendDirection(Number(target.dataset.tailDirection) as TailChaseDirection);
      return;
    }
    if (target.hasAttribute('data-tail-boost')) {
      activateBoost();
      return;
    }
    const action = target.dataset.partyAction;
    if (action === 'refresh-public') {
      notice = '';
      publicRoomsLoaded = false;
      renderSurface(true);
      network.requestPublicRooms();
    } else if (action === 'copy-invite') void copyInvite();
    else if (action === 'share-invite') void shareInvite();
    else if (action === 'ready') network.setReady(!(ownRoomPlayer()?.ready ?? false));
    else if (action === 'start') network.start();
    else if (action === 'retry-connection') network.reconnect();
    else if (action === 'leave') {
      if (room?.phase === 'playing' && !window.confirm('진행 중인 꼬리잡기에서 나갈까요?')) return;
      network.leave();
      room = null;
      currentView = null;
      boostPending = false;
      notice = '방에서 나왔습니다.';
      updateUrl(null);
      renderSurface(true);
      if (entryTab === 'public') window.setTimeout(() => network.requestPublicRooms(), 50);
    } else if (action === 'rematch') network.rematch();
    else if (action === 'reload') window.location.reload();
  });

  document.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-party-action="copy-code"]');
    if (target && room) void copyPartyText(room.roomCode);
  });

  window.addEventListener('keydown', (event) => {
    if ((event.target as HTMLElement).closest('input, textarea, select, [contenteditable="true"]')) return;
    const key = event.key.toLowerCase();
    let direction: TailChaseDirection | null = null;
    if (event.key === 'ArrowUp' || key === 'w') direction = 3;
    else if (event.key === 'ArrowRight' || key === 'd') direction = 0;
    else if (event.key === 'ArrowDown' || key === 's') direction = 1;
    else if (event.key === 'ArrowLeft' || key === 'a') direction = 2;
    if (direction !== null) {
      event.preventDefault();
      if (!event.repeat) sendDirection(direction);
    } else if (event.code === 'Space') {
      event.preventDefault();
      if (!event.repeat) activateBoost();
    }
  });
  window.addEventListener('resize', resizeArenaCanvas);
  window.addEventListener('beforeunload', () => network.dispose());

  function frame(now: number): void {
    drawArena(now);
    requestAnimationFrame(frame);
  }

  window.setInterval(updateArenaHud, 150);
  window.setInterval(() => network.ping(), 15_000);
  window.setInterval(() => {
    if (!room && entryTab === 'public') network.requestPublicRooms();
  }, 4_000);
  updateConnection();
  renderSurface(true);
  if (incomingRoom) network.resumeRoom(incomingRoom, savedPartyNickname());
  requestAnimationFrame(frame);
}
